/**
 * Home page — state-switched landing per Home v1 spec, redesigned
 * 2026-07-26 per docs/superpowers/specs/2026-07-26-homepage-redesign-design.md.
 *
 * Unauth or signed-in-without-roadmap: the full redesigned anonymous funnel
 * (NewUserHero w/ ProblemCard → MetricsBand → HowItWorksSection →
 * OutputsSection → PathsSection → EvidenceLeadSection → EvidenceSection →
 * HomePricingSection → ProductGrid). Signed-in-with-roadmap: ReturningHero
 * (unchanged) + ProductGrid only — the new marketing sections below are for
 * visitors who haven't converted yet.
 */

import React from "react";
import { Box, Container, Skeleton, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  getLatestRoadmap,
  getRoadmapTaskCompletions,
  RoadmapResponse,
  RoadmapTaskCompletion,
} from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { useHashScroll } from "../hooks/useHashScroll";
import WorkshopBanner from "../components/home/WorkshopBanner";
import NewUserHero from "../components/home/NewUserHero";
import ReturningHero from "../components/home/ReturningHero";
import ProductGrid from "../components/home/ProductGrid";
import MetricsBand from "../components/home/MetricsBand";
import HowItWorksSection from "../components/home/HowItWorksSection";
import OutputsSection from "../components/home/OutputsSection";
import PathsSection from "../components/home/PathsSection";
import EvidenceLeadSection from "../components/home/EvidenceLeadSection";
import EvidenceSection from "../components/home/EvidenceSection";
import HomePricingSection from "../components/home/HomePricingSection";

export default function Dashboard() {
  useHashScroll();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const { data: roadmap, isLoading: roadmapLoading } = useQuery<RoadmapResponse>({
    queryKey: ["home-latest-roadmap"],
    queryFn: getLatestRoadmap,
    enabled: isAuthenticated,
    retry: false,
  });

  const { data: completions } = useQuery<RoadmapTaskCompletion[]>({
    queryKey: ["roadmap-task-completions", roadmap?.id],
    queryFn: () => getRoadmapTaskCompletions(roadmap!.id),
    enabled: Boolean(roadmap?.id),
    retry: false,
  });

  const showReturnHero = isAuthenticated && !!roadmap;
  const showLoadingSkeleton = authLoading || (isAuthenticated && roadmapLoading);

  return (
    <Box>
      <WorkshopBanner />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={{ xs: 6, md: 9 }}>
          {showLoadingSkeleton ? (
            <Skeleton variant="rounded" height={320} sx={{ borderRadius: "16px" }} />
          ) : showReturnHero ? (
            <ReturningHero roadmap={roadmap!} completions={completions ?? []} />
          ) : (
            <>
              <NewUserHero />
              <MetricsBand />
              <HowItWorksSection />
              <OutputsSection />
              <PathsSection />
              <EvidenceLeadSection />
              <EvidenceSection />
              <HomePricingSection />
            </>
          )}
          <ProductGrid />
        </Stack>
      </Container>
    </Box>
  );
}
